// PitchedContentCardUtils.ts

import type {
    Card,
    ContentSalesforceObjectsAssociation,
} from "@amzn/ads-blink-client";
import { SalesforceObjectType } from "@amzn/ads-blink-client";

import type { AssociatedPitchedContent } from "../store/editContent";
import type { CardDetail } from "../types/card";
import { CardDetailsSource } from "../types/card";

import { formatDate } from "./date.utils";
import {
    ACTIVITY_NOT_CREATED_DROPDOWN_LABEL,
    OPPORTUNITY_NOT_CREATED_DROPDOWN_LABEL,
    OpportunityStatusText,
} from "./salesforce.utils";
import { formatAsCurrency } from "./string.utils";

export const PITCHED_CONTENTS_CARD_LABEL_OPPORTUNITY = "Opportunity";
export const PITCHED_CONTENTS_CARD_LABEL_ACTIVITY = "Activity";
export const PITCHED_CONTENTS_CARD_LABEL_CREATION_DATE = "Creation Date";
export const PITCHED_CONTENTS_CARD_LABEL_LAST_MODIFIED = "Last Modified";
export const PITCHED_CONTENTS_CARD_LABEL_SALESFORCE_ACCOUNT =
    "Salesforce Account";
export const PITCHED_CONTENTS_CARD_LABEL_STATUS = "Status";
export const PITCHED_CONTENTS_CARD_LABEL_TOTAL_OPPORTUNITY_VALUE =
    "Total Opportunity Value";
export const PITCHED_CONTENTS_CARD_LABEL_NOTES = "Notes";

const getBaseDetails = (
    creationDate: string | undefined,
    lastModifiedDate: string | undefined,
): CardDetail[] => [
    {
        label: PITCHED_CONTENTS_CARD_LABEL_CREATION_DATE,
        value: formatDate(creationDate),
        renderOn: [CardDetailsSource.SIDEPANEL],
    },
    {
        label: PITCHED_CONTENTS_CARD_LABEL_LAST_MODIFIED,
        value: formatDate(lastModifiedDate),
        renderOn: [CardDetailsSource.SIDEPANEL],
    },
];

const getActivityDetails = (
    association: ContentSalesforceObjectsAssociation,
): CardDetail[] => [
    {
        label: PITCHED_CONTENTS_CARD_LABEL_SALESFORCE_ACCOUNT,
        value: association?.salesforceAccountMetadata?.accountName ?? "-",
        renderOn: [CardDetailsSource.CARD, CardDetailsSource.SIDEPANEL],
    },
    {
        label: PITCHED_CONTENTS_CARD_LABEL_OPPORTUNITY,
        value: OPPORTUNITY_NOT_CREATED_DROPDOWN_LABEL,
        renderOn: [CardDetailsSource.CARD, CardDetailsSource.SIDEPANEL],
    },
    {
        label: PITCHED_CONTENTS_CARD_LABEL_ACTIVITY,
        value:
            association?.salesforceActivityMetadata?.activityName ??
            ACTIVITY_NOT_CREATED_DROPDOWN_LABEL,
        renderOn: [CardDetailsSource.CARD, CardDetailsSource.SIDEPANEL],
    },
];

const getOpportunityDetailsFromPLM = (
    association: ContentSalesforceObjectsAssociation,
): CardDetail[] => {
    const details: CardDetail[] = [
        {
            label: PITCHED_CONTENTS_CARD_LABEL_SALESFORCE_ACCOUNT,
            value: association?.salesforceAccountMetadata?.accountName ?? "-",
            renderOn: [CardDetailsSource.CARD, CardDetailsSource.SIDEPANEL],
        },
        {
            label: PITCHED_CONTENTS_CARD_LABEL_OPPORTUNITY,
            value:
                association?.salesforceOpportunityMetadata?.opportunityName ??
                OPPORTUNITY_NOT_CREATED_DROPDOWN_LABEL,
            renderOn: [CardDetailsSource.CARD, CardDetailsSource.SIDEPANEL],
        },
    ];

    if (association?.salesforceOpportunityMetadata) {
        details.push(
            {
                label: PITCHED_CONTENTS_CARD_LABEL_STATUS,
                value: association?.salesforceOpportunityMetadata
                    .opportunityStatus
                    ? OpportunityStatusText[
                          association?.salesforceOpportunityMetadata
                              .opportunityStatus
                      ]
                    : "-",
                renderOn: [CardDetailsSource.CARD, CardDetailsSource.SIDEPANEL],
            },
            {
                label: PITCHED_CONTENTS_CARD_LABEL_TOTAL_OPPORTUNITY_VALUE,
                value: formatAsCurrency(
                    association?.salesforceOpportunityMetadata
                        .opportunityAmount,
                    association?.salesforceOpportunityMetadata
                        .opportunityCurrency,
                ),
                renderOn: [CardDetailsSource.SIDEPANEL],
            },
        );
    }

    return details;
};

const getNoteDetails = (contentNote?: string): CardDetail[] =>
    contentNote
        ? [
              {
                  label: PITCHED_CONTENTS_CARD_LABEL_NOTES,
                  value: contentNote,
                  renderOn: [CardDetailsSource.SIDEPANEL],
              },
          ]
        : [];

export const getPitchedContentDetails = (
    content: AssociatedPitchedContent,
): CardDetail[] => {
    const {
        creationDate,
        lastModifiedDate,
        contentNote,
        accountOpportunities,
        associations,
    } = content;
    const accountOpportunity = accountOpportunities?.[0];
    const association = associations[0];

    const hasActivityAssociation =
        SalesforceObjectType.ACTIVITY ===
        association?.salesforceAssociation?.salesforceObjectType;

    let details = getBaseDetails(creationDate, lastModifiedDate);

    if (hasActivityAssociation) {
        details = [...getActivityDetails(association), ...details];
    } else {
        details = [...getOpportunityDetailsFromPLM(association), ...details];
    }

    return [
        {
            label: "Content Type",
            value: "Client Presentations",
        },
        ...details,
        ...getNoteDetails(contentNote),
    ];
};

export const convertCardToPitchedContent = (
    content: Card,
): AssociatedPitchedContent => {
    const metadata = content.details?.metadata ?? {};
    const opportunityId = metadata?.["salesforceOpportunity"]?.["id"]?.[0];
    const activityId = metadata?.["salesforceActivity"]?.["id"]?.[0];
    const salesforceObjectType: SalesforceObjectType = opportunityId
        ? SalesforceObjectType.OPPORTUNITY
        : activityId
          ? SalesforceObjectType.ACTIVITY
          : SalesforceObjectType.ACCOUNT_ONLY;
    const activityName = metadata?.["salesforceActivity"]?.["displayName"]?.[0];
    const activityDate = metadata?.["activityDate"]?.["displayName"]?.[0];
    const accountName = metadata?.["salesforceAccount"]?.["displayName"]?.[0];
    const opportunityName =
        metadata?.["salesforceOpportunity"]?.["displayName"]?.[0];
    const opportunityStatus =
        metadata?.["opportunityStatus"]?.["displayName"]?.[0];

    return {
        contentId: content.identifier,
        contentTitle: content.title,
        creationDate: metadata?.["creationDate"],
        lastModifiedDate: content.lastModifiedDate,
        fileFormat: metadata?.["fileFormat"],
        contentNote: metadata?.["contentNote"],
        accountOpportunities: [
            {
                accountName:
                    metadata?.["salesforceAccount"]?.["displayName"]?.[0],
                opportunityName,
                opportunityStatus: Object.keys(OpportunityStatusText).find(
                    (key) => OpportunityStatusText[key] === opportunityStatus,
                ),
                totalAmount: metadata?.["totalRevenue"]?.["displayName"]?.[0],
                currencyIsoCode:
                    metadata?.["currencyISOCode"]?.["displayName"]?.[0],
            },
        ],
        associations: [
            {
                salesforceAssociation: {
                    salesforceObjectType,
                },
                salesforceActivityMetadata: {
                    activityName:
                        [activityName, activityDate]
                            .filter((x) => x)
                            .join(" | ") || undefined,
                },
                salesforceAccountMetadata: {
                    accountName,
                },
            },
        ],
    } as AssociatedPitchedContent;
};
